import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { uploadFileWithRetry, deleteFile } from '../services/uploadFileService';
import { restoreFile as restoreFileAPI } from '../services/restoreFileService';
import { submitTramite } from '../services/submitTramiteService';
import { FILE_STATUS, UPLOAD_CONFIG, isFileStatusValidForSubmit } from '../constants/fileStatus';
import { validarTramite } from '../services/validarTramiteService';
import { SessionExpiredError } from '../services/SessionExpiredError';

/**
 * Hook principal para el componente UploadTramitesSAT
 * Maneja carga automática de archivos, estados y submit final
 *
 * @param {Object} params - Parámetros del hook
 * @param {Function} params.setMessage - Función para mostrar mensajes al usuario
 * @returns {Object} Estado y métodos del componente
 */
const useUploadTramitesSAT = ({ setMessage }) => {
  // Estado para datos del trámite
  const [tramiteData, setTramiteData] = useState(null);

  // Estado de carga inicial
  const [isLoading, setIsLoading] = useState(true);

  // Estado de error
  const [error, setError] = useState(null);

  // Token extraído de URL
  const [token, setToken] = useState(null);

  // Session Token extraído de tramiteData
  const [sessionToken, setSessionToken] = useState(null);

  // Estado para archivos con metadata completa
  // Estructura: { [idDocumentacion]: [FileMetadata, ...] }
  // FileMetadata: { file, status, uploadProgress, error, retryCount, fileId, storagePath, fileName }
  const [documentacionFiles, setDocumentacionFiles] = useState({});
  const documentacionFilesRef = useRef(documentacionFiles);

  useEffect(() => {
    documentacionFilesRef.current = documentacionFiles;
  }, [documentacionFiles]);

  // Estado de envío del formulario
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado de éxito del trámite
  const [isSuccess, setIsSuccess] = useState(false);

  // Respuesta del envío (para mostrar ID de trámite)
  const [submissionResponse, setSubmissionResponse] = useState(null);

  // Estado de sesión expirada
  const [sessionExpired, setSessionExpired] = useState(null);

  // Indica si el usuario realizó alguna modificación (subió o eliminó archivos)
  const [hasUserModifications, setHasUserModifications] = useState(false);

  // Inicializar React Hook Form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      afiliado: '',
      tipoTramite: '',
      solicitudCobertura: '',
    },
  });

  /**
   * Extrae el token del query parameter en la URL
   */
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');

    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError('El enlace proporcionado no es válido o ha expirado.');
      setIsLoading(false);
      setMessage({
        open: true,
        message: 'El enlace proporcionado no es válido o ha expirado.',
        severity: 'error',
      });
    }
  }, [setMessage]);

  /**
   * Carga los datos del trámite desde la API usando el token
   */
  useEffect(() => {
    const fetchTramiteData = async () => {
      if (!token) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await validarTramite(token);
        const data = response;

        setTramiteData({
          ...data,
          solicitudCobertura: {
            ...data.solicitudCobertura,
            idSolicitudCoberturaFormateado: formatNumeroSolicitud(
              data.solicitudCobertura?.idSolicitudCobertura,
            ),
          },
        });
        setSessionToken(data.tokenSesion || null);

        // Rellenar campos del formulario
        if (data.afiliado) {
          const { numeroDocumentoString, apellido, nombre } = data.afiliado;
          setValue('afiliado', `${numeroDocumentoString} - ${apellido}, ${nombre}`);
        }

        if (data.tipoCobertura) {
          setValue('tipoTramite', data.tipoCobertura.descripcion);
        }

        if (data.solicitudCobertura) {
          setValue('solicitudCobertura', data.solicitudCobertura.idSolicitudCobertura);
        }

        // Inicializar estructura de archivos por tipo de documentación
        if (data.documentacionTipoCobertura && Array.isArray(data.documentacionTipoCobertura)) {
          const initialFiles = {};
          data.documentacionTipoCobertura.forEach((doc) => {
            if (doc.activo) {
              // Mapear archivos existentes/deleted a estructura de FileMetadata.
              // Los archivos con status 'pending' que vienen del backend se filtran:
              // no se muestran al usuario, no cuentan para el límite y no se envían en el submit.
              const existingFiles = (doc.files || [])
                .filter((file) => file.status !== FILE_STATUS.PENDING)
                .map((file) => ({
                  file: null, // No hay objeto File para archivos del backend
                  status: file.status, // 'existing', 'uploaded', 'deleted'
                  uploadProgress: 100,
                  retryCount: 0,
                  error: null,
                  fileId: file.fileId,
                  storagePath: file.storagePath || '',
                  fileName: file.fileName || 'sin archivo',
                  size: file.size || 0,
                  uploadedAt: file.uploadedAt,
                  originalStatus: null, // Para guardar estado antes de delete
                  idHistorialDocumentacionSolicitudCobertura:
                    file.idHistorialDocumentacionSolicitudCobertura,
                }));

              initialFiles[doc.idDocumentacion] = existingFiles;
            }
          });
          setDocumentacionFiles(initialFiles);
        }

        setIsLoading(false);
      } catch (err) {
        console.error('Error al cargar datos del trámite:', err);

        if (err instanceof SessionExpiredError) {
          setSessionExpired(err.message);
          setIsLoading(false);
          return;
        }

        setError('No se pudieron cargar los datos del trámite.');
        setIsLoading(false);
        setMessage({
          open: true,
          message: `Error al cargar los datos del trámite: ${err.message}`,
          severity: 'error',
        });
      }
    };

    fetchTramiteData();
  }, [token, setValue, setMessage]);

  const formatNumeroSolicitud = (numero) => {
    if (numero === null || numero === undefined) {
      return 'N/A';
    }

    const numParsed = Number(numero);
    if (!Number.isInteger(numParsed)) {
      return numero;
    }

    return numParsed.toLocaleString('es-ES');
  };

  /**
   * Actualiza el estado de un archivo específico
   */
  const updateFileStatus = useCallback((idDocumentacion, fileIndex, updates) => {
    setDocumentacionFiles((prev) => {
      const files = [...(prev[idDocumentacion] || [])];
      if (files[fileIndex]) {
        files[fileIndex] = { ...files[fileIndex], ...updates };
      }
      return { ...prev, [idDocumentacion]: files };
    });
  }, []);

  /**
   * Sube un archivo automáticamente al backend
   */
  const uploadFileAutomatically = useCallback(
    async (idDocumentacion, fileIndex) => {
      const files = documentacionFilesRef.current[idDocumentacion];
      if (!files || !files[fileIndex]) return;

      const fileMetadata = files[fileIndex];

      // Actualizar a 'uploading'
      updateFileStatus(idDocumentacion, fileIndex, {
        status: FILE_STATUS.UPLOADING,
        uploadProgress: 0,
        error: null,
      });

      try {
        const result = await uploadFileWithRetry(
          fileMetadata.file,
          sessionToken,
          idDocumentacion,
          (progress) => {
            updateFileStatus(idDocumentacion, fileIndex, {
              uploadProgress: progress,
            });
          },
          fileMetadata.retryCount || 0,
        );

        // Actualizar con datos del backend
        updateFileStatus(idDocumentacion, fileIndex, {
          status: FILE_STATUS.UPLOADED,
          fileId: result.fileId,
          storagePath: result.storagePath,
          fileName: result.fileName,
          uploadProgress: 100,
          error: null,
        });

        setMessage({
          open: true,
          message: `Archivo "${fileMetadata.file.name}" subido correctamente`,
          severity: 'success',
        });
      } catch (error) {
        console.error('Error al subir archivo:', error);

        if (error instanceof SessionExpiredError) {
          setSessionExpired(error.message);
          return;
        }

        updateFileStatus(idDocumentacion, fileIndex, {
          status: FILE_STATUS.ERROR,
          error: 'Error al subir el archivo, vuelva a intentarlo.',
          uploadProgress: 0,
        });

        setMessage({
          open: true,
          message: `Error al subir "${fileMetadata.file.name}": ${error.message}`,
          severity: 'error',
        });
      }
    },
    [sessionToken, updateFileStatus, setMessage],
  );

  /**
   * Valida el formato de un archivo
   */
  const validateFileFormat = useCallback((file) => {
    const validFormats = UPLOAD_CONFIG.ALLOWED_FORMATS;
    const validExtensions = UPLOAD_CONFIG.ALLOWED_EXTENSIONS;

    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    const isValidFormat =
      validFormats.includes(file.type) || validExtensions.includes(fileExtension);

    if (!isValidFormat) {
      return {
        valid: false,
        error: 'Formato de archivo inválido. Solo se permiten archivos PDF, JPG o JPEG.',
      };
    }

    return { valid: true, error: null };
  }, []);

  /**
   * Valida el tamaño total de archivos
   */
  const validateFileSize = useCallback((idDocumentacion, newFiles = []) => {
    const maxSizeBytes = UPLOAD_CONFIG.MAX_FILE_SIZE;

    const existingFiles = documentacionFilesRef.current[idDocumentacion] || [];
    const allFiles = [...existingFiles, ...newFiles];

    const totalSize = allFiles.reduce((sum, fileOrMetadata) => {
      const file = fileOrMetadata.file || fileOrMetadata;
      return sum + (file.size || 0);
    }, 0);

    if (totalSize > maxSizeBytes) {
      const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
      return {
        valid: false,
        error: `El tamaño total de los archivos (${totalSizeMB}MB) excede el máximo permitido de 4MB.`,
      };
    }

    return { valid: true, error: null };
  }, []);

  /**
   * Valida la cantidad de archivos
   */
  const validateFileCount = useCallback((idDocumentacion, newFiles = []) => {
    const maxFiles = UPLOAD_CONFIG.MAX_FILES_PER_DOC;
    const existingFiles = documentacionFilesRef.current[idDocumentacion] || [];
    const totalCount = existingFiles.length + newFiles.length;

    if (totalCount > maxFiles) {
      return {
        valid: false,
        error: `Solo se pueden subir un máximo de ${maxFiles} archivos por tipo de documentación.`,
      };
    }

    return { valid: true, error: null };
  }, []);

  /**
   * useEffect para detectar archivos en PENDING y subirlos automáticamente
   * Esta solución es más robusta que usar setTimeout ya que:
   * - Se ejecuta después de que el estado se actualiza completamente
   * - Funciona tanto para imágenes como para PDFs
   * - No depende de timings arbitrarios
   */
  useEffect(() => {
    if (!sessionToken) return;

    // Buscar archivos en estado PENDING
    Object.entries(documentacionFiles).forEach(([idDocumentacion, files]) => {
      files.forEach((fileMetadata, index) => {
        if (fileMetadata.status === FILE_STATUS.PENDING && !!fileMetadata?.file?.name) {
          // Subir archivo automáticamente
          uploadFileAutomatically(Number(idDocumentacion), index);
        }
      });
    });
  }, [documentacionFiles, sessionToken, uploadFileAutomatically]);

  /**
   * Agrega archivos en estado PENDING (el useEffect los subirá automáticamente)
   */
  const addFiles = useCallback(
    async (idDocumentacion, newFiles) => {
      // Validar formato de cada archivo
      for (const file of newFiles) {
        const formatValidation = validateFileFormat(file);
        if (!formatValidation.valid) {
          setMessage({
            open: true,
            message: formatValidation.error,
            severity: 'error',
          });
          return { success: false, error: formatValidation.error };
        }
      }

      // Validar cantidad
      const countValidation = validateFileCount(idDocumentacion, newFiles);
      if (!countValidation.valid) {
        setMessage({
          open: true,
          message: countValidation.error,
          severity: 'warning',
        });
        return { success: false, error: countValidation.error };
      }

      // Validar tamaño
      const sizeValidation = validateFileSize(idDocumentacion, newFiles);
      if (!sizeValidation.valid) {
        setMessage({
          open: true,
          message: sizeValidation.error,
          severity: 'warning',
        });
        return { success: false, error: sizeValidation.error };
      }

      // Agregar archivos en estado 'pending'
      const existingFiles = documentacionFilesRef.current[idDocumentacion] || [];

      const newFilesWithMetadata = newFiles.map((file) => ({
        file,
        status: FILE_STATUS.PENDING,
        uploadProgress: 0,
        retryCount: 0,
        error: null,
        fileId: null,
        storagePath: null,
        fileName: file.name,
      }));

      setDocumentacionFiles((prev) => ({
        ...prev,
        [idDocumentacion]: [...existingFiles, ...newFilesWithMetadata],
      }));

      // Marcar que el usuario realizó una modificación
      setHasUserModifications(true);

      // Los archivos se subirán automáticamente vía useEffect cuando detecte status PENDING

      return { success: true, error: null };
    },
    [validateFileFormat, validateFileCount, validateFileSize, setMessage],
  );

  /**
   * Reintenta subir un archivo que falló
   */
  const retryFile = useCallback(
    async (idDocumentacion, fileIndex) => {
      const files = documentacionFilesRef.current[idDocumentacion];
      if (!files || !files[fileIndex]) return;

      const fileMetadata = files[fileIndex];

      updateFileStatus(idDocumentacion, fileIndex, {
        retryCount: (fileMetadata.retryCount || 0) + 1,
        error: null,
      });

      await uploadFileAutomatically(idDocumentacion, fileIndex);
    },
    [updateFileStatus, uploadFileAutomatically],
  );

  /**
   * Marca un archivo como deleted (soft-delete)
   * El archivo permanece visible con opción de deshacer
   */
  const removeFile = useCallback(
    async (idDocumentacion, fileIndex) => {
      const files = documentacionFilesRef.current[idDocumentacion];
      if (!files || !files[fileIndex]) return;

      const fileMetadata = files[fileIndex];

      // No permitir eliminar archivos en uploading
      if (fileMetadata.status === FILE_STATUS.UPLOADING) {
        setMessage({
          open: true,
          message: 'No se puede eliminar un archivo mientras se está subiendo',
          severity: 'warning',
        });
        return;
      }

      // Si el archivo tiene fileId, hacer soft-delete en backend
      if (fileMetadata.fileId) {
        try {
          await deleteFile(sessionToken, fileMetadata.fileId);

          // Marcar como DELETED pero mantener en el array
          updateFileStatus(idDocumentacion, fileIndex, {
            status: FILE_STATUS.DELETED,
            originalStatus: fileMetadata.status, // Guardar estado original
          });

          setMessage({
            open: true,
            message: 'Archivo marcado para eliminación. Puede deshacerlo antes del envío.',
            severity: 'info',
          });

          // Marcar que el usuario realizó una modificación
          setHasUserModifications(true);
        } catch (error) {
          if (error instanceof SessionExpiredError) {
            setSessionExpired(error.message);
            return;
          }

          updateFileStatus(idDocumentacion, fileIndex, {
            status: FILE_STATUS.ERROR,
            error: `Error al eliminar: ${error.message}`,
            errorType: 'delete',
          });

          setMessage({
            open: true,
            message: `Error al eliminar archivo: ${error.message}`,
            severity: 'error',
          });
        }
      } else {
        // Si no tiene fileId, simplemente removerlo del estado (nunca se subió)
        setDocumentacionFiles((prev) => ({
          ...prev,
          [idDocumentacion]: prev[idDocumentacion].filter((_, index) => index !== fileIndex),
        }));

        setMessage({
          open: true,
          message: 'Archivo removido',
          severity: 'info',
        });

        // Marcar que el usuario realizó una modificación
        setHasUserModifications(true);
      }
    },
    [sessionToken, updateFileStatus, setMessage],
  );

  /**
   * Restaura un archivo marcado como deleted
   */
  const restoreFile = useCallback(
    async (idDocumentacion, fileIndex) => {
      const files = documentacionFilesRef.current[idDocumentacion];
      if (!files || !files[fileIndex]) return;

      const fileMetadata = files[fileIndex];

      if (fileMetadata.status !== FILE_STATUS.DELETED) {
        setMessage({
          open: true,
          message: 'Solo se pueden restaurar archivos eliminados',
          severity: 'warning',
        });
        return;
      }

      if (!fileMetadata.fileId) {
        setMessage({
          open: true,
          message: 'No se puede restaurar este archivo',
          severity: 'error',
        });
        return;
      }

      try {
        const response = await restoreFileAPI(sessionToken, fileMetadata.fileId);

        // Usar el estado que devuelve el API (ej: 'existing')
        updateFileStatus(idDocumentacion, fileIndex, {
          status: response.status || fileMetadata.originalStatus || FILE_STATUS.UPLOADED,
          originalStatus: null,
          error: null,
          errorType: null,
        });

        setMessage({
          open: true,
          message: 'Archivo restaurado correctamente',
          severity: 'success',
        });
      } catch (error) {
        if (error instanceof SessionExpiredError) {
          setSessionExpired(error.message);
          return;
        }

        setMessage({
          open: true,
          message: `Error al restaurar archivo: ${error.message}`,
          severity: 'error',
        });
      }
    },
    [sessionToken, updateFileStatus, setMessage],
  );

  /**
   * Verifica si se puede enviar el formulario
   */
  const canSubmit = useCallback(() => {
    // Verificar que no haya archivos en estado pendiente o uploading
    const allFiles = Object.values(documentacionFiles).flat();
    const hasInvalidStatus = allFiles.some(
      (f) =>
        f.status !== FILE_STATUS.UPLOADED &&
        f.status !== FILE_STATUS.EXISTING &&
        f.status !== FILE_STATUS.ERROR &&
        f.status !== FILE_STATUS.DELETED, // DELETED es válido
    );

    if (hasInvalidStatus) {
      return {
        can: false,
        reason: 'Hay archivos pendientes de subir o en proceso',
      };
    }

    // Verificar que el usuario haya realizado al menos una modificación
    if (!hasUserModifications) {
      return {
        can: false,
        reason: 'Debe subir o eliminar al menos un archivo para enviar el trámite',
      };
    }

    return { can: true, reason: null };
  }, [hasUserModifications, documentacionFiles]);

  /**
   * Maneja el envío del formulario
   */
  const onSubmit = useCallback(
    async (formData) => {
      // Validar que se pueda enviar
      const submitValidation = canSubmit();
      if (!submitValidation.can) {
        setMessage({
          open: true,
          message: submitValidation.reason,
          severity: 'error',
        });
        return;
      }

      setIsSubmitting(true);

      try {
        // Construir documentación con metadata de archivos subidos
        const documentacion = tramiteData.documentacionTipoCobertura
          .filter((doc) => doc.activo)
          .map((doc) => {
            const files = (documentacionFilesRef.current[doc.idDocumentacion] || [])
              .filter((f) => isFileStatusValidForSubmit(f.status))
              .map((f) => ({
                fileId: f.fileId,
                status: f.status,
                fileName: f.fileName,
                storagePath: f.storagePath,
              }));

            return {
              idDocumentacion: doc.idDocumentacion,
              documentacion: doc.documentacion,
              requerido: doc.requerido,
              files,
            };
          })
          .filter((doc) => doc.files.length > 0); // Solo incluir docs con archivos

        // Enviar trámite
        const response = await submitTramite(
          {
            afiliado: tramiteData.afiliado,
            tipoCobertura: tramiteData.tipoCobertura,
            solicitudCobertura: tramiteData.solicitudCobertura,
            status: 'submitted',
            documentacionTipoCobertura: documentacion,
          },
          sessionToken,
        );

        setMessage({
          open: true,
          message: response.message || 'Trámite enviado correctamente',
          severity: 'success',
        });

        // Marcar como exitoso y guardar respuesta
        setSubmissionResponse(response);
        setIsSuccess(true);

        // Opcional: Limpiar formulario o redirigir
      } catch (err) {
        console.error('Error al enviar el trámite:', err);

        if (err instanceof SessionExpiredError) {
          setSessionExpired(err.message);
          return;
        }

        setMessage({
          open: true,
          message: `Error al enviar el trámite: ${err.message}`,
          severity: 'error',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [tramiteData, sessionToken, canSubmit, setMessage],
  );

  return {
    // Estado
    tramiteData,
    isLoading,
    error,
    token,
    documentacionFiles,
    isSubmitting,

    // React Hook Form
    control,
    handleSubmit,
    errors,
    watch,

    // Métodos
    addFiles,
    removeFile,
    retryFile,
    restoreFile,
    onSubmit,
    canSubmit,

    // Validaciones (exportadas para usar en UI)
    validateFileFormat,
    validateFileSize,
    validateFileCount,

    // Estados de éxito
    isSuccess,
    submissionResponse,

    // Estado de sesión expirada
    sessionExpired,
  };
};

export default useUploadTramitesSAT;
